import PropTypes from 'prop-types';
import { Amplify } from 'aws-amplify';
import * as Sentry from "@sentry/react";
import { AMPLIFY_API } from 'src/config-global';
import { CookieStorage } from 'aws-amplify/utils';
import AuthService from 'src/services/AuthService';
import { cookieStorageOptions } from "src/data/cookie-domain";
import apiClient, { lambdaClient } from 'src/services/ApiService';
import { useMemo, useEffect, useReducer, useCallback } from 'react';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { accessibleCompanies } from '@heyjob-dash/shared/company/onboarding/model';
import { getStorage, setStorage } from '@heyjob-dash/shared/hooks/use-local-storage';
import {
  signIn,
  signUp,
  signOut,
  confirmSignUp,
  resetPassword,
  confirmSignIn,
  getCurrentUser,
  resendSignUpCode,
  fetchAuthSession,
  fetchMFAPreference,
  fetchUserAttributes, confirmResetPassword
} from 'aws-amplify/auth';

import { identifyUser, resetAnalytics, setCompanyContext } from 'src/utils/analytics';

import { AuthContext } from './auth-context';

// ----------------------------------------------------------------------
/**
 * NOTE:
 * We only build demo at basic level.
 * Customer will need to do some extra handling yourself if you want to extend the logic and other features...
 */
// ----------------------------------------------------------------------

/**
 * DOCS: https://docs.amplify.aws/react/build-a-backend/auth/manage-user-session/
 */
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: `${AMPLIFY_API.userPoolId}`,
      userPoolClientId: `${AMPLIFY_API.userPoolWebClientId}`,
    },
  },
});

if (Object.keys(cookieStorageOptions).includes(import.meta.env.VITE_ENV)) {
  cognitoUserPoolsTokenProvider.setKeyValueStorage(
    new CookieStorage(cookieStorageOptions[import.meta.env.VITE_ENV])
  );
}

const initialState = {
  user: null,
  loading: true,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
    };
  }
  if (action.type === 'COMPANY') {
    return {
      ...state,
      user: {
        ...state.user,
        data: {
          ...state.user.data,
          current_company: action.payload,
          selectedCompany: action.payload,
        }
      }
    };
  }
  return state;
};

const AUTH_STORAGE_KEY = 'company_settings';

// ----------------------------------------------------------------------

export function AuthProvider({ children }) {
  const [curState, dispatch] = useReducer(
    reducer,
    initialState,
    () => initialState
  );

  const initialize = useCallback(async () => {
    try {
      const { userId: currentUser } = await getCurrentUser();

      const userAttributes = await fetchUserAttributes();

      const { idToken, accessToken } = (await fetchAuthSession()).tokens ?? {};

      const groups = accessToken.payload['cognito:groups'];

      if (currentUser) {
        if (groups && groups.length > 0 && (groups.includes('Azienda') || groups.includes('Responsabile') || groups.includes('Timesheet') || groups.includes('ResponsabileSicurezza'))) {
          apiClient.interceptors.request.use(async (config) => {
            const curIdToken =
              (await fetchAuthSession())?.tokens?.idToken ?? idToken;
            config.headers.Authorization = `Bearer ${curIdToken}`;
            return config;
          });
          lambdaClient.interceptors.request.use(async (config) => {
            const curIdToken =
              (await fetchAuthSession())?.tokens?.idToken ?? idToken;
            config.headers.Authorization = `Bearer ${curIdToken}`;
            return config;
          });

          const state = getStorage(AUTH_STORAGE_KEY) || {};

          let userDataRes = {}
          try {
            userDataRes = await AuthService.getUserData();
          } catch (error) {
            await signOut();
            resetAnalytics();
            dispatch({
              type: 'INITIAL',
              payload: {
                user: null,
              },
            });
            return;
          }

          if (import.meta.env.VITE_ENV !== 'local') {
            // Set user in Sentry
            Sentry.setUser({
              id: userAttributes.sub,
              email: userAttributes.email,
            });
          }

          const activeCompanies = accessibleCompanies(userDataRes?.data?.user?.companies);
          const defaultCompany = activeCompanies.length > 0 ? activeCompanies[0] : null;
          const currentCompany = state?.company && activeCompanies.includes(state?.company.toString()) ? state?.company : defaultCompany;

          identifyUser(
            userAttributes,
            { ...userDataRes.data.user, current_company: currentCompany },
            groups
          );

          dispatch({
            type: 'INITIAL',
            payload: {
              user: {
                ...userAttributes,
                id: userAttributes.sub,
                displayName: `${userAttributes.given_name} ${userAttributes.family_name}`,
                idToken,
                accessToken,
                groups,
                // role: 'admin',
                data: {
                  ...userDataRes.data.user,
                  current_company: currentCompany,
                  selectedCompany: currentCompany,
                },
              },
            },
          });
        } else {
          await signOut();
          resetAnalytics();

          dispatch({
            type: 'INITIAL',
            payload: {
              user: null,
            },
          });
        }
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      resetAnalytics();
      dispatch({
        type: 'INITIAL',
        payload: {
          user: null,
        },
      });
    }
  }, [dispatch]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const {
      isSignedIn,
      nextStep
    } = await signIn({
      username: email,
      password,
    });

    switch (nextStep.signInStep) {
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        return nextStep.signInStep;
      case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
        return nextStep.signInStep;
      case 'DONE':
      default:
        break;
    }

    if (isSignedIn) {

      const userAttributes = await fetchUserAttributes();

      const { idToken, accessToken } = (await fetchAuthSession()).tokens ?? {};

      const groups = accessToken.payload['cognito:groups'];

      if (groups && groups.length > 0 && (groups.includes('Azienda') || groups.includes('Responsabile') || groups.includes('Timesheet') || groups.includes('ResponsabileSicurezza'))) {
        apiClient.interceptors.request.use(async (config) => {
          const curIdToken = (await fetchAuthSession()).tokens.idToken ?? idToken;
          config.headers.Authorization = `Bearer ${curIdToken}`;
          return config;
        });
        lambdaClient.interceptors.request.use(async (config) => {
          const curIdToken = (await fetchAuthSession()).tokens.idToken ?? idToken;
          config.headers.Authorization = `Bearer ${curIdToken}`;
          return config;
        });

        const userDataRes = await AuthService.getUserData();
        const state = getStorage(AUTH_STORAGE_KEY) || {};
        state.company = Object.keys(userDataRes.data.user.companies)[0];
        setStorage(AUTH_STORAGE_KEY, state);

        if (import.meta.env.VITE_ENV !== 'local') {
          // Set user in Sentry
          Sentry.setUser({
            id: userAttributes.sub,
            email: userAttributes.email,
          });
        }

        const activeCompanies = accessibleCompanies(userDataRes?.data?.user?.companies);
        const defaultCompany = activeCompanies.length > 0 ? activeCompanies[0] : null;

        identifyUser(
          userAttributes,
          { ...userDataRes.data.user, current_company: defaultCompany },
          groups
        );

        dispatch({
          type: 'INITIAL',
          payload: {
            user: {
              ...userAttributes,
              id: userAttributes.sub,
              displayName: `${userAttributes.given_name} ${userAttributes.family_name}`,
              idToken,
              accessToken,
              groups,
              data: {
                ...userDataRes.data.user,
                selectedCompany: defaultCompany,
                current_company: defaultCompany,
              },
            },
          },
        });
      } else {
        await signOut();
        resetAnalytics();

        throw new Error('Utente non autorizzato ad accedere al portale');
      }
    }

    return 'DONE';
  }, []);

  // FETCH USER DATA
  const fetchUserData = useCallback(async () => {
    const userDataRes = await AuthService.getUserData();
    dispatch({
      type: "REFRESH_USER_DATA",
      payload: {
        data: {
          ...curState.user.data,
          ...userDataRes.data.user,
        },
      },
    });
  }, [curState]);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    await signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
          given_name: firstName,
          family_name: lastName,
        },
      },
    });
  }, []);

  const confirmLogin = useCallback(async (newPassword) => {
    await confirmSignIn({
      challengeResponse: newPassword,
    });

    const userAttributes = await fetchUserAttributes();

    const { idToken, accessToken } = (await fetchAuthSession()).tokens ?? {};

    const groups = accessToken.payload['cognito:groups'];

    if (groups && groups.length > 0 && (groups.includes('Azienda') || groups.includes('Responsabile'))) {
      apiClient.interceptors.request.use(async (config) => {
        const curIdToken = (await fetchAuthSession()).tokens.idToken ?? idToken;
        config.headers.Authorization = `Bearer ${curIdToken}`;
        return config;
      });
      lambdaClient.interceptors.request.use(async (config) => {
        const curIdToken = (await fetchAuthSession()).tokens.idToken ?? idToken;
        config.headers.Authorization = `Bearer ${curIdToken}`;
        return config;
      });

      const userDataRes = await AuthService.getUserData();
      const state = getStorage(AUTH_STORAGE_KEY) || {};
      state.company = Object.keys(userDataRes.data.user.companies)[0];
      setStorage(AUTH_STORAGE_KEY, state);

      if (import.meta.env.VITE_ENV !== 'local') {
        // Set user in Sentry
        Sentry.setUser({
          id: userAttributes.sub,
          email: userAttributes.email,
        });
      }

      const activeCompanies = accessibleCompanies(userDataRes?.data?.user?.companies);
      const defaultCompany = activeCompanies.length > 0 ? activeCompanies[0] : null;

      identifyUser(
        userAttributes,
        { ...userDataRes.data.user, current_company: defaultCompany },
        groups
      );

      dispatch({
        type: 'INITIAL',
        payload: {
          user: {
            ...userAttributes,
            id: userAttributes.sub,
            displayName: `${userAttributes.given_name} ${userAttributes.family_name}`,
            idToken,
            accessToken,
            groups,
            data: {
              ...userDataRes.data.user,
              selectedCompany: defaultCompany,
              current_company: defaultCompany,
            },
          },
        },
      });
    } else {
      await signOut();
      resetAnalytics();

      throw new Error('Utente non autorizzato ad accedere al portale');
    }
  }, []);

  // CONFIRM REGISTER
  const confirmRegister = useCallback(async (email, code) => {
    await confirmSignUp({
      username: email,
      confirmationCode: code,
    });
  }, []);

  // RESEND CODE REGISTER
  const resendCodeRegister = useCallback(async (email) => {
    await resendSignUpCode({
      username: email,
    });
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    await signOut();
    resetAnalytics();
    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  // FORGOT PASSWORD
  const forgotPassword = useCallback(async (email) => {
    await resetPassword({ username: email });
  }, []);

  // NEW PASSWORD
  const newPassword = useCallback(async (email, code, password) => {
    await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword: password,
    });
  }, []);

  const getAuthDetails = useCallback(async () => {
    const session = await fetchAuthSession();
    const mfaPreference = await fetchMFAPreference();
    return {
      ...session,
      mfa: mfaPreference,
    };
  }, []);

  const selectCompany = useCallback(async (companyId) => {
    // store in local storage
    const state = getStorage(AUTH_STORAGE_KEY) || {};
    state.company = companyId;
    setStorage(AUTH_STORAGE_KEY, state);

    setCompanyContext(
      { ...curState.user?.data, current_company: companyId },
      curState.user?.groups
    );

    dispatch({
      type: 'COMPANY',
      payload: companyId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = curState.user ? 'authenticated' : 'unauthenticated';

  const status = curState.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: curState.user,
      method: 'amplify',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      logout,
      register,
      fetchUserData,
      confirmLogin,
      newPassword,
      getAuthDetails,
      forgotPassword,
      confirmRegister,
      resendCodeRegister,
      selectCompany,
    }),
    [
      status,
      curState.user,
      //
      login,
      logout,
      register,
      confirmLogin,
      fetchUserData,
      newPassword,
      getAuthDetails,
      forgotPassword,
      confirmRegister,
      resendCodeRegister,
      selectCompany,
    ]
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
